'use client';

import { useCallback } from 'react';

import { useAuiState } from '@assistant-ui/react';

import { useConfigValue, useTenantName } from '@/components/providers/config-provider';
import { useSafePostHog } from '@/hooks/use-safe-posthog';
import { clearResumableRecord, getResumableRecord } from '@/lib/hitl/resumable-storage';
import { SYNC_EVENTS } from '@/lib/sync/events';
import { useCrossTabSync } from '@/lib/sync/hooks/use-cross-tab-sync';
import { resolveResourceId } from '@/lib/utils/resource-id';
import { findThread } from '@/lib/utils/thread';
import { chatApiBaseUrl } from '@/lib/website-auth/browser';

/**
 * User-initiated STOP for resumable tenants. The chat route deliberately detaches the run from
 * the request socket (a refresh must keep it alive for resume), so the composer's fetch abort
 * only severs the client — the model keeps generating server-side. The returned handler
 * additionally cancels the server run (`POST /:tenant/chat/cancel/:streamId`) and clears the
 * resume record so a later refresh doesn't resume the cancelled stream.
 *
 * Wired to the stop button's click ONLY — never to fetch aborts or unload (a refresh also
 * aborts the fetch, and refresh must keep the run resumable).
 */
export function useResumableStreamCancel(): () => void {
  const resumableEnabled = useConfigValue<boolean>('resumableStreaming') === true;
  const tenantName = useTenantName();
  const posthog = useSafePostHog();
  const { emit } = useCrossTabSync();
  const mainThreadId = useAuiState(state => state.threads.mainThreadId);
  const threadItems = useAuiState(state => state.threads.threadItems);

  return useCallback(() => {
    if (!resumableEnabled) return;
    const record = getResumableRecord(tenantName);
    if (!record) return;
    // The stop button acts on the ACTIVE thread; a record owned by another thread's
    // (background) run must survive.
    const activeThreadUuid = mainThreadId
      ? (findThread(threadItems, mainThreadId)?.remoteId ?? mainThreadId)
      : null;
    if (activeThreadUuid && activeThreadUuid !== record.threadUuid) return;

    clearResumableRecord(tenantName);
    // Tell sibling tabs tailing this thread to stop too (they're resuming the now-cancelled run).
    emit(SYNC_EVENTS.RESUMABLE_STREAM_STOPPED, { threadUuid: record.threadUuid });
    const resourceId = resolveResourceId(posthog?.get_distinct_id());
    const url = `${chatApiBaseUrl(tenantName)}/${tenantName}/chat/cancel/${encodeURIComponent(
      record.streamId,
    )}?resourceId=${encodeURIComponent(resourceId)}`;
    void fetch(url, { method: 'POST', credentials: 'include' }).catch(() => {
      // Best-effort: the client stream is already severed either way.
    });
  }, [resumableEnabled, tenantName, posthog, mainThreadId, threadItems, emit]);
}
